import React, { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "components/table";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getPlacedOrder } from "store/orderSlice";
import { ORDER_STATUS } from "constants";
import Button from "components/button";

import OrderConfirmModal from "./OrderConfirmModal";
import AddOrderItem from "./AddOrderItem";
import OrderStatusModal from "../../draft-orders/components/OrderStatusModal";
import DeleteIOrder from "./DeleteIOrder";

const columnHelper = createColumnHelper();

export default function OrderTable({ status }) {
  const dispatch = useDispatch();
  const { order: { draft = {}, afterDraft = {} } = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const { placedData, placedError, placedLoading } = useSelector(
    (state) => state.orders
  );

  const {
    isDraft,
    isPlaced,
    isProcessing,
    isFulfilled,
    isDelivered,
    isCancelled,
  } = useMemo(
    () => ({
      isDraft: status?.enumVal === ORDER_STATUS.DRAFT,
      isPlaced: status?.enumVal === ORDER_STATUS.PLACED,
      isProcessing: status?.enumVal === ORDER_STATUS.PROCESSING,
      isFulfilled: status?.enumVal === ORDER_STATUS.FULFILLED,
      isDelivered: status?.enumVal === ORDER_STATUS.DELIVERED,
      isCancelled: status?.enumVal === ORDER_STATUS.CANCELLED,
    }),
    [status]
  );

  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const refreshList = () => {
    setRefreshTrigger((prev) => !prev);
  };

  useEffect(() => {
    const page = pagination.pageIndex + 1;
    const limit = pagination.pageSize;

    dispatch(
      getPlacedOrder({
        page,
        limit,
        search: searchText,
        status: status?.enumVal,
      })
    );
  }, [
    dispatch,
    pagination.pageIndex,
    pagination.pageSize,
    searchText,
    refreshTrigger,
    status,
  ]);
  const totalPages = useMemo(() => {
    const totalCount = placedData?.total ?? 0;
    const pageSize = pagination.pageSize;
    if (!pageSize || pageSize <= 0) return 0;
    return Math.ceil(totalCount / pageSize);
  }, [placedData?.total, pagination.pageSize]);
  const columns = [
    // Order Number
    columnHelper.accessor("orderNo", {
      id: "orderNo",
      header: "ORDER NO",
      enableSorting: true,
      enableHiding: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col">
            <Link
              to={isDraft ? `/orders/edit/${row._id}` : `/orders/${row._id}`}
              className="cursor-pointer text-blue-600 hover:underline"
            >
              {info.getValue()}
            </Link>
            {row?.orderForUser && (
              <span className="text-sm">{row?.orderForUser}</span>
            )}
          </div>
        );
      },
    }),

    // Order Type
    columnHelper.accessor("orderType", {
      id: "orderType",
      header: "ORDER TYPE",
      cell: (info) => info.getValue() ?? "-",
    }),

    // Shipment Type
    columnHelper.accessor("shipmentType", {
      id: "shipmentType",
      header: "SHIPMENT TYPE",
      cell: (info) => info.getValue() ?? "-",
    }),

    // Status
    columnHelper.accessor("status.name", {
      id: "status",
      header: "STATUS",
      cell: (info) => info.getValue() ?? "-",
    }),

    // Description
    columnHelper.accessor("description", {
      id: "description",
      header: "DESCRIPTION",
      cell: (info) => info.getValue() ?? "-",
    }),

    // Created At
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "CREATED AT",
      cell: (info) => {
        const date = new Date(info.getValue());
        return isNaN(date)
          ? info.getValue()
          : date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
      },
    }),

    // Created By
    columnHelper.accessor("createdByUser", {
      id: "createdByUser",
      header: "CREATED BY",
      cell: (info) => info.getValue() ?? "-",
    }),

    // Actions
    columnHelper.display({
      id: "action",
      header: "ACTIONS",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const order = row.original;
        if (!order?._id) return "N/A";
        const canCancel = order.status?.enumVal !== ORDER_STATUS.CANCELLED;

        return (
          <div className="flex gap-3">
            {isDraft && (
              <>
                {draft?.itemAdd && (
                  <AddOrderItem
                    orderId={order._id}
                    refreshList={refreshList}
                    orderType={order.orderType}
                  />
                )}
                {draft?.statusChange && (
                  <OrderStatusModal data={order} refreshList={refreshList} />
                )}
                {draft?.delete && (
                  <DeleteIOrder data={order} refreshList={refreshList} />
                )}
              </>
            )}
            {isPlaced && (
              <>
                {afterDraft.statusChange && canCancel && (
                  <OrderConfirmModal
                    data={order}
                    refreshList={refreshList}
                    isComfirm={false}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    }),
  ];

  // Initial Column Visibility
  const initialColumnVisibility = {
    orderNo: true,
    orderType: true,
    shipmentType: false,
    status: true,
    description: false,
    createdAt: true,
    createdByUser: false,
    action: true,
  };

  const orderData = useMemo(() => placedData?.data ?? [], [placedData]);
  return (
    <DataTable
      title={`${status.name} Orders`}
      columns={columns}
      data={orderData ?? []}
      error={placedError}
      loading={placedLoading}
      totalPages={totalPages}
      total={placedData?.total}
      pagination={pagination}
      setPagination={setPagination}
      isColumnDropdown={true}
      initialColumnVisibility={initialColumnVisibility}
      searchText={searchText}
      setSearchText={setSearchText}
      searchPlaceholder={"Search Order"}
      rightAccessory={
        <div className="flex gap-2">
          {draft?.create && isDraft && (
            <Link to="/orders/create">
              <Button>Create Order</Button>
            </Link>
          )}
        </div>
      }
    />
  );
}
